import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  operatorApi, OPERATOR_KEY_STORAGE,
  type Tenant, type TenantCreate, type TenantUpdate, type ProvisionedAccount, type AccountSpec, type TenantUser,
} from '../api/operator'
import { rolesForModules } from '../api/users'

const MODULE_OPTIONS = [
  { value: 'meter_reading', label: 'Očitavanje vodomera' },
  { value: 'repair',        label: 'Popravke i servisi' },
  { value: 'scada',         label: 'SCADA nadzor' },
]

const MODULE_CHIP: Record<string, string> = {
  meter_reading: 'bg-blue-100 text-blue-700',
  repair:        'bg-amber-100 text-amber-700',
  scada:         'bg-purple-100 text-purple-700',
}

const MODULE_SHORT: Record<string, string> = {
  meter_reading: 'MR',
  repair:        'REP',
  scada:         'SCADA',
}

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function licenseStatus(t: Tenant) {
  if (!t.active) return { label: 'Neaktivan', color: 'bg-red-100 text-red-700' }
  if (!t.license_expires_at) return { label: 'Aktivna', color: 'bg-emerald-100 text-emerald-700' }
  const daysLeft = Math.ceil((new Date(t.license_expires_at).getTime() - Date.now()) / 86_400_000)
  if (daysLeft < 0)   return { label: 'Istekla',     color: 'bg-red-100 text-red-700' }
  if (daysLeft < 30)  return { label: `${daysLeft}d`, color: 'bg-amber-100 text-amber-700' }
  return { label: 'Aktivna', color: 'bg-emerald-100 text-emerald-700' }
}

// ── API key gate ──────────────────────────────────────────────────────────────

function ApiKeyGate({ onKey }: { onKey: (k: string) => void }) {
  const [input, setInput] = useState('')
  return (
    <div className="flex items-center justify-center h-full">
      <div className="bg-white rounded-2xl shadow-xs border border-gray-100 p-8 w-full max-w-sm text-center space-y-4">
        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto">
          <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-900">Operator pristup</h2>
          <p className="text-xs text-gray-500 mt-1">Unesite operator API ključ</p>
        </div>
        <input
          type="password"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && input && onKey(input)}
          placeholder="OPERATOR_API_KEY"
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-hidden"
        />
        <button
          onClick={() => { if (input) onKey(input) }}
          disabled={!input}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
        >
          Prijaviti se
        </button>
      </div>
    </div>
  )
}

// ── Create tenant modal ───────────────────────────────────────────────────────

function CreateTenantModal({ apiKey, onClose }: { apiKey: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<TenantCreate>({
    name: '', municipality: '', seats: 10, modules: [],
  })
  const [err, setErr] = useState('')

  const toggleModule = (m: string) =>
    setForm((f) => ({
      ...f,
      modules: f.modules?.includes(m)
        ? f.modules.filter((x) => x !== m)
        : [...(f.modules ?? []), m],
    }))

  const mutation = useMutation({
    mutationFn: () => operatorApi.createTenant(apiKey, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['op-tenants'] }); onClose() },
    onError: (e: unknown) => setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri kreiranju.'),
  })

  const set = (key: keyof TenantCreate) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: key === 'seats' ? Number(e.target.value) : e.target.value }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-base font-bold text-gray-900 mb-5">Novi tenant</h2>

        <div className="space-y-4">
          {[
            { key: 'name'          as const, label: 'Naziv organizacije', required: true },
            { key: 'municipality'  as const, label: 'Opština / grad',     required: true },
            { key: 'contact_email' as const, label: 'Kontakt email',      required: false },
            { key: 'contact_phone' as const, label: 'Kontakt telefon',    required: false },
          ].map(({ key, label, required }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {label}{required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              <input type="text" value={form[key] as string ?? ''} onChange={set(key)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-hidden" />
            </div>
          ))}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Broj sedišta <span className="text-red-500">*</span></label>
              <input type="number" min={1} max={500} value={form.seats} onChange={set('seats')}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-hidden" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Licenca ističe</label>
              <input type="date" value={form.license_expires_at ?? ''} onChange={set('license_expires_at')}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-hidden" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Moduli</label>
            <div className="flex flex-col gap-2">
              {MODULE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.modules?.includes(value) ?? false}
                    onChange={() => toggleModule(value)}
                    className="rounded-sm text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {err && <p className="mt-3 text-xs text-red-600">{err}</p>}

        <div className="flex gap-2 mt-6">
          <button
            onClick={() => { setErr(''); mutation.mutate() }}
            disabled={mutation.isPending || !form.name || !form.municipality}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2.5 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Kreiranje…' : 'Kreirati tenant'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Provision accounts modal ──────────────────────────────────────────────────

function ProvisionModal({
  apiKey, tenant, onClose,
}: { apiKey: string; tenant: Tenant; onClose: () => void }) {
  const [specs, setSpecs] = useState<AccountSpec[]>([
    { roles: ['company_admin'], label: 'Admin' },
  ])
  const [result, setResult] = useState<ProvisionedAccount[] | null>(null)
  const [err, setErr] = useState('')

  const mutation = useMutation({
    mutationFn: () => operatorApi.provisionAccounts(apiKey, tenant.id, specs),
    onSuccess: (r) => setResult(r.data),
    onError: (e: unknown) => setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  const addSpec = () => setSpecs((s) => [...s, { roles: ['meter_reader'], label: '' }])
  const removeSpec = (i: number) => setSpecs((s) => s.filter((_, idx) => idx !== i))
  const updateSpec = (i: number, field: keyof AccountSpec, val: string | string[]) =>
    setSpecs((s) => s.map((sp, idx) => idx === i ? { ...sp, [field]: val } : sp))
  const toggleRole = (i: number, role: string) =>
    setSpecs((s) => s.map((sp, idx) => {
      if (idx !== i) return sp
      const roles = sp.roles.includes(role)
        ? sp.roles.filter((r) => r !== role)
        : [...sp.roles, role]
      return { ...sp, roles }
    }))

  const handleDownload = () => {
    const url = operatorApi.qrBundleUrl(tenant.id)
    const a = document.createElement('a')
    a.href = url
    a.target = '_blank'
    a.rel = 'noreferrer'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-base font-bold text-gray-900 mb-1">Provajdovati naloge</h2>
        <p className="text-xs text-gray-500 mb-5">{tenant.name} · {tenant.municipality}</p>

        {result ? (
          /* Success state */
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold text-emerald-800">
                {result.length} naloga kreiran{result.length > 1 ? 'o' : ''}
              </p>
            </div>

            <div className="space-y-2">
              {result.map((acc, i) => {
                // Extract token UUID from qr_data (waterworks://activate/{uuid})
                const tokenId = acc.activation_token ?? acc.qr_data.split('/').pop() ?? ''
                const webLink = `${window.location.origin}/activate?token=${tokenId}`
                return (
                  <div key={acc.user_id} className="border border-gray-200 rounded-xl p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">{acc.label ?? `Nalog ${i + 1}`}</span>
                      <span className="text-xs text-gray-500">{acc.roles.join(', ')}</span>
                    </div>
                    {/* Web activation link */}
                    <div className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-lg px-2.5 py-1.5">
                      <svg className="w-3.5 h-3.5 text-purple-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <p className="text-xs font-mono text-purple-700 break-all flex-1">{webLink}</p>
                      <button
                        onClick={() => navigator.clipboard.writeText(webLink)}
                        title="Kopirati link"
                        className="shrink-0 text-purple-400 hover:text-purple-700"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-xs font-mono text-gray-400 break-all">{acc.qr_data}</p>
                  </div>
                )
              })}
            </div>

            <button
              onClick={handleDownload}
              className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold py-2.5 rounded-lg"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Preuzeti QR bundle (PDF)
            </button>
            <button onClick={onClose}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 rounded-lg">
              Zatvoriti
            </button>
          </div>
        ) : (
          /* Form state */
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Dodajte naloge koje želite da kreirate. Svaki nalog dobija QR kod za aktivaciju.</p>

            {specs.map((spec, i) => (
              <div key={i} className="border border-gray-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-700">Nalog {i + 1}</p>
                  {specs.length > 1 && (
                    <button onClick={() => removeSpec(i)} className="text-xs text-red-500 hover:text-red-700">Ukloniti</button>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Opis (opciono)</label>
                  <input type="text" value={spec.label ?? ''} onChange={(e) => updateSpec(i, 'label', e.target.value)}
                    placeholder="npr. Meračar za Zonu A"
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-hidden" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Role</label>
                  <div className="flex flex-wrap gap-1.5">
                    {rolesForModules(tenant.modules ?? []).map(({ value, label }) => {
                      const active = spec.roles.includes(value)
                      return (
                        <button key={value} onClick={() => toggleRole(i, value)}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                            active ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
                          }`}>
                          {label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}

            <button onClick={addSpec}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-purple-400 text-gray-500 hover:text-purple-600 rounded-xl py-2.5 text-sm font-medium transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Dodati nalog
            </button>

            {err && <p className="text-xs text-red-600">{err}</p>}

            <div className="flex gap-2">
              <button
                onClick={() => { setErr(''); mutation.mutate() }}
                disabled={mutation.isPending || specs.some((s) => s.roles.length === 0)}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
              >
                {mutation.isPending ? 'Kreiranje…' : `Kreirati ${specs.length} nalog${specs.length > 1 ? 'a' : ''}`}
              </button>
              <button onClick={onClose}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
                Otkazati
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Reset password modal ──────────────────────────────────────────────────────

function ResetPasswordModal({
  apiKey, tenant, user, onClose,
}: { apiKey: string; tenant: Tenant; user: TenantUser; onClose: () => void }) {
  const [pw, setPw]   = useState('')
  const [err, setErr] = useState('')
  const [ok, setOk]   = useState(false)

  const mutation = useMutation({
    mutationFn: () => operatorApi.resetPassword(apiKey, tenant.id, user.id, pw),
    onSuccess: () => setOk(true),
    onError: (e: unknown) => setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div>
          <h2 className="text-base font-bold text-gray-900">Reset lozinke</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {user.email ?? 'Neaktivan nalog'} · {tenant.name}
          </p>
        </div>

        {ok ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <svg className="w-5 h-5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-semibold text-emerald-800">Lozinka je resetovana.</p>
            </div>
            <button onClick={onClose} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
              Zatvoriti
            </button>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nova lozinka</label>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && pw.length >= 8 && mutation.mutate()}
                placeholder="Minimum 8 karaktera"
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-hidden"
              />
            </div>
            {err && <p className="text-xs text-red-600">{err}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => { setErr(''); mutation.mutate() }}
                disabled={mutation.isPending || pw.length < 8}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
              >
                {mutation.isPending ? 'Resetovanje…' : 'Resetovati'}
              </button>
              <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
                Otkazati
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Edit tenant modal ─────────────────────────────────────────────────────────

function EditTenantModal({
  apiKey, tenant, onClose,
}: { apiKey: string; tenant: Tenant; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<TenantUpdate>({
    modules:            tenant.modules ?? [],
    seats:              tenant.seats,
    license_expires_at: tenant.license_expires_at ?? undefined,
    active:             tenant.active,
  })
  const [err, setErr] = useState('')

  const toggleModule = (m: string) =>
    setForm((f) => ({
      ...f,
      modules: f.modules?.includes(m)
        ? f.modules.filter((x) => x !== m)
        : [...(f.modules ?? []), m],
    }))

  const mutation = useMutation({
    mutationFn: () => operatorApi.updateTenant(apiKey, tenant.id, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['op-tenants'] }); onClose() },
    onError: (e: unknown) => setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri izmeni.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-base font-bold text-gray-900 mb-1">Izmeni tenant</h2>
        <p className="text-xs text-gray-500 mb-5">{tenant.name} · {tenant.municipality}</p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Broj sedišta</label>
              <input
                type="number" min={1} max={500} value={form.seats ?? tenant.seats}
                onChange={(e) => setForm((f) => ({ ...f, seats: Number(e.target.value) }))}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-hidden"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Licenca ističe</label>
              <input
                type="date" value={form.license_expires_at ? form.license_expires_at.split('T')[0] : ''}
                onChange={(e) => setForm((f) => ({ ...f, license_expires_at: e.target.value || undefined }))}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-hidden"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Moduli</label>
            <div className="flex flex-col gap-2">
              {MODULE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.modules?.includes(value) ?? false}
                    onChange={() => toggleModule(value)}
                    className="rounded-sm text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.active ?? tenant.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                className="rounded-sm text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Tenant aktivan</span>
            </label>
          </div>
        </div>

        {err && <p className="mt-3 text-xs text-red-600">{err}</p>}

        <div className="flex gap-2 mt-6">
          <button
            onClick={() => { setErr(''); mutation.mutate() }}
            disabled={mutation.isPending}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2.5 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Čuvanje…' : 'Sačuvati izmene'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Tenant user list (expandable) ─────────────────────────────────────────────

function TenantUserList({
  apiKey, tenant, onReset,
}: { apiKey: string; tenant: Tenant; onReset: (u: TenantUser) => void }) {
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['op-users', tenant.id],
    queryFn: () => operatorApi.listUsers(apiKey, tenant.id).then((r) => r.data),
    staleTime: 30_000,
  })

  if (isLoading) {
    return (
      <tr><td colSpan={6} className="px-8 py-4">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
          Učitavanje korisnika…
        </div>
      </td></tr>
    )
  }

  return (
    <tr>
      <td colSpan={6} className="px-0 py-0">
        <div className="bg-gray-50 border-t border-b border-gray-100 px-8 py-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Korisnici · {users.length}
          </p>
          <div className="space-y-1.5">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-100 px-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${u.activated_at ? 'bg-emerald-400' : 'bg-gray-300'}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {u.full_name ?? (u.email ?? '—')}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {u.email ?? 'Nije aktiviran'} · {u.roles.join(', ')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => onReset(u)}
                  className="shrink-0 ml-4 text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-2.5 py-1.5 rounded-lg transition-colors"
                >
                  Reset lozinke
                </button>
              </div>
            ))}
          </div>
        </div>
      </td>
    </tr>
  )
}

// ── Tenant row ────────────────────────────────────────────────────────────────

function TenantRow({
  tenant, apiKey, onProvision, onEdit,
}: { tenant: Tenant; apiKey: string; onProvision: (t: Tenant) => void; onEdit: (t: Tenant) => void }) {
  const { label, color } = licenseStatus(tenant)
  const [expanded, setExpanded]           = useState(false)
  const [resetting, setResetting]         = useState<TenantUser | null>(null)

  const handleQrDownload = () => {
    fetch(operatorApi.qrBundleUrl(tenant.id), { headers: { 'X-Operator-Key': apiKey } })
      .then((r) => {
        if (!r.ok) throw new Error('No pending accounts or invalid key')
        return r.blob()
      })
      .then((blob) => {
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `qr-${tenant.municipality.toLowerCase()}.pdf`
        a.click()
      })
      .catch((e) => alert(e.message))
  }

  return (
    <>
      <tr className="hover:bg-gray-50 border-b border-gray-100">
        <td className="px-6 py-4">
          <button onClick={() => setExpanded((x) => !x)} className="text-left">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              {tenant.name}
            </p>
            <p className="text-xs text-gray-500 ml-5">{tenant.municipality}</p>
          </button>
        </td>
        <td className="px-6 py-4 text-sm text-gray-700">{tenant.seats}</td>
        <td className="px-6 py-4 text-sm text-gray-500">{fmtDate(tenant.license_expires_at)}</td>
        <td className="px-6 py-4">
          <div className="flex flex-wrap gap-1">
            {(tenant.modules ?? []).length === 0
              ? <span className="text-xs text-gray-400">—</span>
              : (tenant.modules ?? []).map((m) => (
                  <span key={m} className={`inline-block px-1.5 py-0.5 rounded-sm text-xs font-semibold ${MODULE_CHIP[m] ?? 'bg-gray-100 text-gray-600'}`}>
                    {MODULE_SHORT[m] ?? m}
                  </span>
                ))
            }
          </div>
        </td>
        <td className="px-6 py-4">
          <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-semibold ${color}`}>{label}</span>
        </td>
        <td className="px-6 py-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(tenant)}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors"
            >
              Izmeni
            </button>
            <button
              onClick={() => onProvision(tenant)}
              className="text-xs font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-2 py-1.5 rounded-lg transition-colors"
            >
              Provajdovati
            </button>
            <button
              onClick={handleQrDownload}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors"
              title="Preuzmi QR bundle za neaktivirane naloge"
            >
              QR PDF
            </button>
          </div>
        </td>
      </tr>

      {expanded && (
        <TenantUserList apiKey={apiKey} tenant={tenant} onReset={setResetting} />
      )}

      {resetting && (
        <ResetPasswordModal
          apiKey={apiKey}
          tenant={tenant}
          user={resetting}
          onClose={() => setResetting(null)}
        />
      )}
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function OperatorPage() {
  const qc = useQueryClient()
  const [apiKey, setApiKey] = useState<string>(
    () => localStorage.getItem(OPERATOR_KEY_STORAGE) ?? ''
  )
  const [creatingTenant, setCreatingTenant]   = useState(false)
  const [provisioning, setProvisioning]       = useState<Tenant | null>(null)
  const [editing, setEditing]                 = useState<Tenant | null>(null)

  const { data: tenants = [], isLoading, isError } = useQuery({
    queryKey: ['op-tenants', apiKey],
    queryFn: () => operatorApi.listTenants(apiKey).then((r) => r.data),
    enabled: !!apiKey,
    staleTime: 30_000,
    retry: false,
  })

  // If query fails, clear key so user can re-enter
  const handleKey = (k: string) => {
    localStorage.setItem(OPERATOR_KEY_STORAGE, k)
    setApiKey(k)
    qc.invalidateQueries({ queryKey: ['op-tenants'] })
  }

  const clearKey = () => {
    localStorage.removeItem(OPERATOR_KEY_STORAGE)
    setApiKey('')
  }

  if (!apiKey || isError) {
    if (isError) { clearKey() }
    return (
      <div className="h-full">
        <ApiKeyGate onKey={handleKey} />
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs bg-purple-100 text-purple-700 font-semibold px-2.5 py-1 rounded-full">
            Operator pristup
          </span>
          <button onClick={clearKey} className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2">
            Odjaviti se
          </button>
        </div>
        <button
          onClick={() => setCreatingTenant(true)}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Novi tenant
        </button>
      </div>

      {/* Tenants table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Tenanti</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {isLoading ? 'Učitavanje…' : `${tenants.length} organizacija`}
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-7 h-7 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-sm">Nema tenanta. Kreirajte prvog.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Naziv</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sedišta</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Licenca ističe</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Moduli</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <TenantRow
                    key={t.id}
                    tenant={t}
                    apiKey={apiKey}
                    onProvision={setProvisioning}
                    onEdit={setEditing}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creatingTenant && (
        <CreateTenantModal apiKey={apiKey} onClose={() => setCreatingTenant(false)} />
      )}
      {provisioning && (
        <ProvisionModal apiKey={apiKey} tenant={provisioning} onClose={() => setProvisioning(null)} />
      )}
      {editing && (
        <EditTenantModal apiKey={apiKey} tenant={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  )
}
